import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import { useAuth } from './useAuth';
import type { UserEarlySignalsInterests } from '../types';

export function useUserEarlySignalsInterests() {
  const { user, companyId } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery<UserEarlySignalsInterests | null>({
    queryKey: ['user_early_signals_interests', user?.id, companyId],
    queryFn: async () => {
      if (!user?.id || !companyId) return null;
      const { data, error } = await supabase
        .from('user_early_signals_interests')
        .select('*')
        .eq('user_id', user.id)
        .eq('company_id', companyId)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      return {
        ...data,
        followed_supplier_ids: (data.followed_supplier_ids as string[]) ?? [],
        topics: (data.topics as string[]) ?? [],
        regions: (data.regions as string[]) ?? [],
      } as UserEarlySignalsInterests;
    },
    enabled: !!user?.id && !!companyId,
    staleTime: 60000,
  });

  const upsertMutation = useMutation({
    mutationFn: async (updates: Partial<Pick<UserEarlySignalsInterests, 'followed_supplier_ids' | 'topics' | 'regions'>>) => {
      if (!user?.id || !companyId) throw new Error('User and company required');
      const { data, error } = await supabase
        .from('user_early_signals_interests')
        .upsert(
          {
            user_id: user.id,
            company_id: companyId,
            followed_supplier_ids: updates.followed_supplier_ids ?? query.data?.followed_supplier_ids ?? [],
            topics: updates.topics ?? query.data?.topics ?? [],
            regions: updates.regions ?? query.data?.regions ?? [],
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'user_id,company_id' }
        )
        .select()
        .single();
      if (error) throw error;
      return data as UserEarlySignalsInterests;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user_early_signals_interests', user?.id, companyId] });
    },
  });

  return {
    interests: query.data ?? null,
    loading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    updateInterests: upsertMutation.mutateAsync,
  };
}
